import type {
    ComponentProps,
    ElementType,
    PropsWithChildren,
    ReactElement,
    ReactNode,
} from 'react';
import { useEffect, useRef, useState } from 'react';

import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

interface TruncatedTextProps extends ComponentProps<typeof Typography> {
    /** Tooltip content shown when the text is truncated. Defaults to `children`. */
    tooltipTitle?: ReactNode;
    /** Renders the underlying element as this tag/component (e.g. `"span"`). */
    component?: ElementType;
}

/**
 * Text that truncates with an ellipsis when it runs out of room and reveals its
 * full value in a tooltip only while truncated. Detects overflow via a
 * ResizeObserver so it reacts to column/container resizing.
 * @requires ThemeProvider - Needs Material-UI theme context
 */
const TruncatedText = ({
    tooltipTitle,
    children,
    ...typographyProps
}: PropsWithChildren<TruncatedTextProps>): ReactElement => {
    const textRef = useRef<HTMLElement>(null);

    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const element = textRef.current;

        if (!element) return;

        const checkTruncation = (): void => {
            setIsTruncated(element.scrollWidth > element.clientWidth);
        };

        // ResizeObserver fires once on observe and again on every resize, unlike window.resize.
        const truncationObserver = new ResizeObserver(checkTruncation);
        truncationObserver.observe(element);

        return (): void => {
            truncationObserver.disconnect();
        };
    }, []);

    return (
        <Tooltip
            title={tooltipTitle ?? children}
            disableHoverListener={!isTruncated}
            disableFocusListener={!isTruncated}
            disableTouchListener={!isTruncated}
        >
            <Typography
                ref={textRef}
                {...typographyProps}
                sx={{ minWidth: 0, ...(typographyProps.sx as object) }}
                noWrap
            >
                {children}
            </Typography>
        </Tooltip>
    );
};

export { TruncatedText };
export type { TruncatedTextProps };
